import React, { useState } from 'react';

import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';

import Button from 'CommonComponents/Button';

export default function AthletesTable({ athletes, nationalities, loading, sportAthletesUpdateUrl, pageUseTopIndex, page, onUpdated, onDelete, onError }) {
    return (
        <div className="tw-relative tw-overflow-hidden tw-border-2 tw-border-grey tw-rounded-lg tw-text-charcoal tw-text-sm">
            <table className="tw-w-full tw-table-auto tw-border-collapse tw-text-left">
                <thead className="tw-bg-blue-25 tw-font-semibold">
                    <tr>
                        <th className="tw-p-4">#</th>
                        <th className="tw-p-4">{$t('First Name')}</th>
                        <th className="tw-p-4">{$t('Last Name')}</th>
                        <th className="tw-p-4">{$t('Nationality')}</th>
                        <th className="tw-p-4">{$t('Gender')}</th>
                        <th className="tw-p-4">{$t('Actions')}</th>
                    </tr>
                </thead>
                {!loading && (
                    <tbody>
                        {athletes.map((athlete, index) => {
                            const updateUrl = sportAthletesUpdateUrl.replace('ATHLETE_ID', athlete.id);

                            return (
                                <AthleteRow
                                    key={`athlete-${index}`}
                                    athlete={athlete}
                                    nationalities={nationalities}
                                    index={page + index + 1}
                                    pageUseTopIndex={pageUseTopIndex}
                                    updateUrl={updateUrl}
                                    onDelete={(athlete) => onDelete(athlete)}
                                    onError={(e) => onError(e)}
                                    onUpdated={() => onUpdated()}
                                />
                            );
                        })}
                    </tbody>
                )}
                {!loading && athletes?.length == 0 && (
                    <tbody>
                        <tr className="tw-border-b-2 last:tw-border-0 tw-border-grey tw-bg-grey/50">
                            <td colSpan="10" className="tw-p-4">
                                {$t('No athletes created')}
                            </td>
                        </tr>
                    </tbody>
                )}
                {loading && (
                    <tbody>
                        <tr className="tw-border-b-2 last:tw-border-0 tw-border-grey tw-bg-grey/50">
                            <td colSpan="5" className="tw-p-4">
                                {$t('Loading...')}
                            </td>
                        </tr>
                    </tbody>
                )}
            </table>
        </div>
    );
}

function AthleteRow({ athlete, index, nationalities, updateUrl, pageUseTopIndex, onUpdated, onDelete, onError }) {
    const [editing, setEditing] = useState(false);

    const toggleEditing = () => {
        setEditing(!editing);
    };

    if (editing) {
        return (
            <AthleteRowEdit
                athlete={athlete}
                index={index}
                nationalities={nationalities}
                updateUrl={updateUrl}
                pageUseTopIndex={pageUseTopIndex}
                onError={(e) => onError(e)}
                onCancel={() => setEditing(false)}
                onUpdated={() => {
                    setEditing(false);
                    onUpdated();
                }}
            />
        );
    }

    return (
        <tr className="tw-cursor-pointer tw-border-b-2 last:tw-border-0 tw-border-grey tw-bg-grey/50">
            <td className="tw-p-4 tw-border-r-2">{index}</td>
            <td className="tw-p-4 tw-border-r-2">{athlete.first_name}</td>
            <td className="tw-p-4 tw-border-r-2">{athlete.last_name}</td>
            <td className="tw-p-4 tw-border-r-2">{athlete.nationality}</td>
            <td className="tw-p-4 tw-border-r-2">{athlete.gender_label}</td>
            <td className="tw-p-4">
                <small className="p-1">
                    <Button small variant="primary" style={{ minWidth: '4rem' }} onClick={() => toggleEditing()}>
                        {$t('Edit')}
                    </Button>
                </small>
                <small className="p-1">
                    <Button small variant="danger" style={{ minWidth: '4rem' }} onClick={() => onDelete(athlete)}>
                        {$t('Delete')}
                    </Button>
                </small>
            </td>
        </tr>
    );
}

const GENDERS = [
    { value: '', label: $t('Undisclosed') },
    { value: 'Male', label: $t('Male') },
    { value: 'Female', label: $t('Female') },
];

function AthleteRowEdit({ athlete, index, nationalities, updateUrl, pageUseTopIndex, onError, onUpdated, onCancel }) {
    const [firstName, setFirstName] = useState(athlete.first_name ?? '');
    const [lastName, setLastName] = useState(athlete.last_name ?? '');
    const [nationality, setNationality] = useState(
        athlete.nationality ? { value: athlete.nationality, label: athlete.nationality } : { value: '', label: $t('Undisclosed') },
    );
    const [gender, setGender] = useState(athlete.gender ? { value: athlete.gender, label: athlete.gender_label } : { value: '', label: $t('Undisclosed') });
    const [request, setRequest] = useState(false);

    const saveAthlete = () => {
        setRequest(true);
        axios
            .put(updateUrl, {
                first_name: firstName,
                last_name: lastName,
                nationality: nationality.value,
                gender: gender.value,
            })
            .then((res) => {
                setRequest(false);
                setFirstName(res.data.athlete.first_name ?? '');
                setLastName(res.data.athlete.last_name ?? '');
                setNationality({ value: res.data.athlete.nationality ?? '', label: res.data.athlete.nationality ?? 'Undisclosed' });
                setGender({ value: res.data.athlete.gender ?? '', label: res.data.athlete.gender_label ?? $t('Undisclosed') });
                onUpdated();
            })
            .catch((error) => {
                setRequest(false);
                onError($t('There was an error updating athlete'));
            });
    };

    const handleChangeField = (event) => {
        switch (event.target.id) {
            case 'firstName':
                setFirstName(event.target.value);
                break;
            case 'lastName':
                setLastName(event.target.value);
                break;
        }
    };

    function handleChangeNationality(option) {
        setNationality(option);
    }

    function handleChangeGender(option) {
        setGender(option);
    }

    return (
        <tr className="tw-border-b-2 last:tw-border-0 tw-border-grey tw-bg-grey/50">
            <td className="tw-p-4 tw-border-r-2">{index}</td>
            <td className="tw-p-4 tw-border-r-2">
                <input
                    type="text"
                    step="1"
                    name={`firstName`}
                    className="form-control"
                    id={`firstName`}
                    value={firstName}
                    onChange={(event) => handleChangeField(event)}
                />
            </td>
            <td className="tw-p-4 tw-border-r-2">
                <input
                    type="text"
                    step="1"
                    name={`lastName`}
                    className="form-control"
                    id={`lastName`}
                    value={lastName}
                    onChange={(event) => handleChangeField(event)}
                />
            </td>
            <td className="tw-p-4 tw-border-r-2">
                <CreatableSelect
                    name="select_nationality"
                    options={nationalities}
                    onChange={handleChangeNationality}
                    defaultValue={{
                        value: '',
                        label: 'Undisclosed',
                    }}
                    value={nationality}
                    menuPlacement={index < pageUseTopIndex ? 'bottom' : 'top'}
                />
            </td>
            <td className="tw-p-4 tw-border-r-2">
                <Select
                    name="select_gender"
                    options={GENDERS}
                    onChange={handleChangeGender}
                    defaultValue={{
                        value: '',
                        label: $t('Undisclosed'),
                    }}
                    value={gender}
                    menuPlacement={index < pageUseTopIndex ? 'bottom' : 'top'}
                />
            </td>
            <td className="tw-p-4">
                <small className="p-1">
                    <Button small variant="danger" style={{ minWidth: '4rem' }} onClick={() => onCancel()}>
                        {$t('Cancel')}
                    </Button>
                </small>
                <small className="p-1">
                    <Button small variant="primary" style={{ minWidth: '4rem' }} onClick={() => saveAthlete()} disabled={request}>
                        {$t('Save')}
                    </Button>
                </small>
            </td>
        </tr>
    );
}
